// The API serialises timestamps already shifted into the business timezone
// (Django TIME_ZONE), e.g. "2026-08-05T17:26:17.780291+08:00". Passing those
// through `new Date()` re-interprets them in the *viewer's* timezone, which
// both shifts the time shown and makes the server and client renders disagree
// — React hydration error #425. Everything here reads the literal fields of
// the string, so the two renders cannot diverge.

// Django TIME_ZONE. Anything formatted with Intl must pin to this, or the
// server renders in the VPS zone and the browser in the viewer's.
export const BUSINESS_TIME_ZONE = "Asia/Kuala_Lumpur";

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const MONTH_LABELS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const ISO = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/;

type Parts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
};

function parse(iso: string | null | undefined): Parts | null {
  if (!iso) return null;
  const m = ISO.exec(iso);
  if (!m) return null;
  return {
    year: Number(m[1]),
    month: Number(m[2]),
    day: Number(m[3]),
    hour: Number(m[4] ?? 0),
    minute: Number(m[5] ?? 0),
  };
}

const pad = (n: number) => String(n).padStart(2, "0");

/** "2026-08-05T17:26:17+08:00" -> "05 Aug 2026" */
export function formatDate(
  iso: string | null | undefined,
  fallback = "—",
): string {
  const p = parse(iso);
  if (!p) return fallback;
  return `${pad(p.day)} ${MONTH_LABELS[p.month - 1]} ${p.year}`;
}

/** "2026-08-05T17:26:17+08:00" -> "05 Aug 2026, 05:26 pm" */
export function formatDateTime(
  iso: string | null | undefined,
  fallback = "—",
): string {
  const p = parse(iso);
  if (!p) return fallback;
  const suffix = p.hour < 12 ? "am" : "pm";
  const hour12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
  return `${formatDate(iso)}, ${pad(hour12)}:${pad(p.minute)} ${suffix}`;
}

/** "2026-08-05T17:26:17+08:00" -> "05:26 PM" */
export function formatTime(
  iso: string | null | undefined,
  fallback = "—",
): string {
  const p = parse(iso);
  if (!p) return fallback;
  const suffix = p.hour < 12 ? "AM" : "PM";
  const hour12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
  return `${pad(hour12)}:${pad(p.minute)} ${suffix}`;
}

/** "2026-08-05T17:26:17+08:00" -> "05 August 2026" */
export function formatDateLong(
  iso: string | null | undefined,
  fallback = "—",
): string {
  const p = parse(iso);
  if (!p) return fallback;
  return `${pad(p.day)} ${MONTH_LABELS_LONG[p.month - 1]} ${p.year}`;
}

/** "2026-08-05T17:26:17+08:00" -> "5 Aug" */
export function formatDayMonth(
  iso: string | null | undefined,
  fallback = "—",
): string {
  const p = parse(iso);
  if (!p) return fallback;
  return `${p.day} ${MONTH_LABELS[p.month - 1]}`;
}

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "2026-08-13" -> "Thu, 13 Aug" */
export function formatWeekdayDayMonth(
  iso: string | null | undefined,
  fallback = "—",
): string {
  const p = parse(iso);
  if (!p) return fallback;
  const weekday = new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay();
  return `${WEEKDAY_LABELS[weekday]}, ${pad(p.day)} ${MONTH_LABELS[p.month - 1]}`;
}

/** Two-line table cell: { top: "05 Aug", bottom: "2026" } */
export function formatDateParts(
  iso: string | null | undefined,
): { top: string; bottom: string } | null {
  const p = parse(iso);
  if (!p) return null;
  return {
    top: `${pad(p.day)} ${MONTH_LABELS[p.month - 1]}`,
    bottom: String(p.year),
  };
}

/** 1-12 -> "Aug" (or "August") */
export function monthLabel(month: number, long = false): string {
  const labels = long ? MONTH_LABELS_LONG : MONTH_LABELS;
  return labels[month - 1] ?? "";
}

/** (2026, 8) -> "Aug 2026" (or "August 2026") */
export function formatMonthYear(
  year: number,
  month: number,
  long = false,
): string {
  return `${monthLabel(month, long)} ${year}`;
}

/** "2026-08-05T17:26:17+08:00" -> "2026-08-05T17:26", the value shape an
 *  <input type="datetime-local"> expects, in business-zone wall time. */
export function toDateTimeInputValue(iso: string | null | undefined): string {
  const p = parse(iso);
  if (!p) return "";
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

/** "YYYY-MM-DD" for today in the business timezone. `new Date()` alone reads the
 *  VPS clock on the server and the viewer's clock in the browser. */
export function businessToday(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: BUSINESS_TIME_ZONE }).format(
    new Date(),
  );
}

/** Wall-clock time in the business timezone, as "HH:MM" (24-hour). */
export function businessClockTime(): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: BUSINESS_TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date());
}

/** Current year in the business timezone. */
export function businessYear(): number {
  return Number(businessToday().slice(0, 4));
}

/** Current month (1-12) in the business timezone. */
export function businessMonth(): number {
  return Number(businessToday().slice(5, 7));
}

/** URL params are user-editable and bookmarkable; a bad year/month must never
 *  reach the API, where a dropped param comes back as an opaque 403. */
export function safeYear(value: unknown): number {
  const n = Number(value);
  return Number.isInteger(n) && n >= 2000 && n <= 2100 ? n : businessYear();
}

export function safeMonth(value: unknown): number {
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 && n <= 12 ? n : businessMonth();
}

/** A `Date` -> "YYYY-MM-DD" in that date's own fields. Server components use
 *  this to hand "today" to client components, so both renders share one value. */
export function toDateKey(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * The last day of a "YYYY-MM-DD" range after its first day moves to `nextStart`. The range keeps
 * its length, as in calendar apps: 10-16 Mar with the first day moved to 11 Mar becomes 11-17 Mar,
 * and a one-day range stays one day. With no valid range yet (a day missing, or the last day before
 * the first) the last day simply becomes the new first day.
 */
export function shiftRangeEnd(previousStart: string, previousEnd: string, nextStart: string): string {
  const next = parse(nextStart);
  if (!next) return previousEnd;
  if (!parse(previousStart) || !parse(previousEnd) || previousEnd < previousStart) return nextStart;
  const shifted = new Date(0);
  // setUTCFullYear, not Date.UTC: a year typed digit by digit passes through 0002, 0020, ...
  shifted.setUTCFullYear(next.year, next.month - 1, next.day + daysBetween(previousStart, previousEnd));
  return `${String(shifted.getUTCFullYear()).padStart(4, "0")}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`;
}

/** Whole days from `fromKey` to `toKey`, both "YYYY-MM-DD". Negative = past. */
export function daysBetween(fromKey: string, toKey: string): number {
  const a = parse(fromKey);
  const b = parse(toKey);
  if (!a || !b) return 0;
  return Math.round(
    (Date.UTC(b.year, b.month - 1, b.day) -
      Date.UTC(a.year, a.month - 1, a.day)) /
      86_400_000,
  );
}
